import React, { PropsWithChildren, ReactNode } from 'react';
import { TolgeeInstance, TolgeeProvider } from '@tolgee/react';
import { RuntimeContextProvider } from 'src/RuntimeContextProvider.tsx';
import { ProjectsContext } from 'src/projects/ProjectsContext.ts';
import { ProjectsRepository } from 'src/projects/ProjectsRepository.ts';
import { ConfigContextProvider } from 'src/ConfigContextProvider';
import { ToastProvider } from 'src/modules/common/components/Toast/ToastProvider';
import { UserSettings } from 'src/types/UserSettings.ts';
import { ContextAwareConfirmation } from 'react-confirm';
import { UserLocalOptionsClientInterface } from 'src/common/UserLocalOptionsClient/UserLocalOptionsClientInterface.ts';

export type AppProvidersProps = PropsWithChildren<{
    tolgee: TolgeeInstance;
    getProjectsRepository: () => ProjectsRepository;
    userSettings: UserSettings;
    shadowRoot: HTMLElement;
    currency: string | null;
    imageProxyUri: string;
    uri: string;
    userOptionsClient: UserLocalOptionsClientInterface;
    /**
     * Wraps the app with the GraphQL/database layer (an ApolloProvider). Kept as a render prop
     * so each entry point can choose where the database comes from: the routed apps load their
     * own offline database (DatabaseProvider), while the embedded projects page receives a
     * ready DatabaseClient from its host (EmbeddedDatabaseProvider).
     */
    renderDatabase: (children: ReactNode) => ReactNode;
}>;

/**
 * The full context/provider tree shared by every VSS entry point. Kept separate from the
 * bootstrap/render logic so both the routed apps (see main.tsx `init`) and the routerless
 * projects page (see `initProjectsPage`) mount inside an identical environment.
 */
export const AppProviders = ({
    tolgee,
    getProjectsRepository,
    userSettings,
    shadowRoot,
    currency,
    imageProxyUri,
    uri,
    renderDatabase,
    userOptionsClient,
    children,
}: AppProvidersProps) => (
    <React.StrictMode>
        <TolgeeProvider
            tolgee={tolgee}
            fallback="Toolge Loading..."
        >
            <ProjectsContext.Provider value={getProjectsRepository}>
                <RuntimeContextProvider
                    userOptionsClient={userOptionsClient}
                    userSettings={userSettings}
                    rootElement={shadowRoot}
                >
                    <ConfigContextProvider
                        userSettings={userSettings}
                        currency={currency}
                        shadowRoot={shadowRoot}
                        imageProxyUri={imageProxyUri}
                        uri={uri}
                    >
                        {renderDatabase(
                            <ToastProvider>
                                <ContextAwareConfirmation.ConfirmationRoot/>
                                {children}
                            </ToastProvider>,
                        )}
                    </ConfigContextProvider>
                </RuntimeContextProvider>
            </ProjectsContext.Provider>
        </TolgeeProvider>
    </React.StrictMode>
);
